import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { View } from 'react-native';

import { InlineAlert, Text, createMqStyles, tokens } from '@fhs/ui';
import Picture from '@fhs-legacy/frontend/src/components/picture';
import { useFeatureDonationQuery } from '@fhs-legacy/frontend/src/generated/sanity-graphql';
import { useFeaturesContext } from '@fhs-legacy/frontend/src/state/features';
import { LaunchDarklyFlag, useFlag } from '@fhs-legacy/frontend/src/state/launchdarkly';
import { DONATION_CONTAINER } from '@fhs-legacy/frontend/src/utils/test-ids';
import { type ISanityImage } from '@rbi-ctg/menu';

import { useDonationCartEntries } from '../../../queries/use-donation-cart-entries';

import DonationButtons from './donation-buttons';
import DonationHeading from './donation-heading';
import DonationContainerSkeleton from './donation-skeleton';

interface IDonationContainerProps {
  isBadContinueAttempt: boolean;
  setIsBadContinueAttempt: React.Dispatch<React.SetStateAction<boolean>>;
}

export function CartDonationContainer({
  isBadContinueAttempt,
  setIsBadContinueAttempt,
}: IDonationContainerProps) {
  const mqStyles = useMqStyles();
  const { formatMessage } = useIntl();

  const { featureDonationId = '', featureIdsLoading } = useFeaturesContext();
  const enableFeatureDonation = useFlag(LaunchDarklyFlag.ENABLE_FEATURE_DONATION);

  const { data, loading } = useFeatureDonationQuery({
    variables: { featureDonationId },
    skip: !featureDonationId || !enableFeatureDonation,
    fetchPolicy: 'cache-first',
  });

  const featureDonation = useMemo(() => data?.FeatureDonation ?? null, [data]);
  const featureDonationLoading = featureIdsLoading || loading;

  const donationCartEntriesQuery = useDonationCartEntries();

  if (featureDonationLoading || donationCartEntriesQuery.isLoading) {
    return <DonationContainerSkeleton />;
  }

  return (
    <View
      testID={DONATION_CONTAINER}
      style={mqStyles.container}
      accessibilityRole="summary"
      accessibilityLabel="Donation selection"
      accessibilityInvalid={isBadContinueAttempt}
      accessibilityErrorMessage={
        isBadContinueAttempt ? 'Please select a donation amount' : undefined
      }
    >
      <DonationHeading
        headerText={featureDonation?.header?.locale ?? ''}
        modalText={featureDonation?.dialogInformation?.subtitle?.locale ?? ''}
      />

      <View
        style={{
          borderColor: isBadContinueAttempt ? tokens.colors.$error : undefined,
          borderWidth: isBadContinueAttempt ? 1 : 0,
          padding: 1,
        }}
      >
        <View style={mqStyles.bodyContainer}>
          <Picture
            width="64px"
            height="64px"
            image={featureDonation?.logoImage?.locale as ISanityImage}
            alt={featureDonation?.header?.locale ?? ''}
            objectFitContain
          />

          <View style={mqStyles.bodyText}>
            <Text
              style={{ color: isBadContinueAttempt ? tokens.colors.$error : tokens.colors.$black }}
            >
              {featureDonation?.subtitle?.locale ?? ''}
            </Text>
          </View>
        </View>

        <DonationButtons
          donationItems={donationCartEntriesQuery.data ?? []}
          setIsBadContinueAttempt={setIsBadContinueAttempt}
        />
      </View>

      {/* Alert for when continue is blocked due to no donation selected */}
      {isBadContinueAttempt && (
        <InlineAlert
          type="error"
          message={formatMessage({
            id: 'selectDonationMissing',
          })}
        />
      )}
    </View>
  );
}

const useMqStyles = createMqStyles({
  container: {
    $base: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      gap: 8,
      overflow: 'hidden',
      position: 'relative',
    },
  },
  bodyContainer: {
    $base: {
      flexDirection: 'row',
      justifyContent: 'space-around',
      alignItems: 'center',
      padding: 1,
      backgroundColor: 'white',
      gap: 2,
    },
  },
  bodyText: {
    $base: {
      paddingHorizontal: 2,
      maxWidth: '80%',
    },
  },
});
